/**
 * Stubs / extension points for cheaper Indian SMS gateways.
 *
 * These providers deliver the OTP SMS themselves, but the app still ends up
 * authenticated with Firebase Auth. The intended pattern:
 *   1. sendOtp   → call gateway REST API (via a Cloud Function proxy to keep
 *                  the gateway API key server-side) to send the code.
 *   2. verifyOtp → send {mobile, code} to a Cloud Function that verifies the
 *                  code and mints a Firebase CUSTOM TOKEN, then
 *                  signInWithCustomToken(auth, token) → UserCredential.
 *
 * SHARED-CONTRACT WISH (flag for Lead Agent → Agent A): to enable any of these,
 * Agent A needs two new callables, e.g. `requestSmsOtp({mobile})` and
 * `verifySmsOtp({mobile, code}) -> { customToken }`. Add to `CALLABLE` in
 * @mitang/shared. Until then these throw.
 *
 * DLT/regulatory note (India): MSG91/Fast2SMS transactional SMS require a
 * DLT-registered sender ID + approved template. See README.
 */
import { signInWithCustomToken, type UserCredential } from 'firebase/auth';
import { auth } from '../firebase';
import type { OtpConfirmation, OtpProvider } from './types';

function notImplemented(providerName: string): never {
  throw new Error(
    `OTP provider "${providerName}" is not implemented yet. It requires server-side callables (requestSmsOtp / verifySmsOtp) from Agent A. See lib/otp/stubProviders.ts.`,
  );
}

/** Shared skeleton for gateway-backed providers that end in a custom token. */
function makeGatewayProvider(providerName: string): OtpProvider {
  return {
    name: providerName,
    async sendOtp(mobileE164: string): Promise<OtpConfirmation> {
      // TODO: await requestSmsOtp callable; return a confirmation carrying a
      // server-issued request id.
      void mobileE164;
      notImplemented(providerName);
    },
    async verifyOtp(confirmation: OtpConfirmation, code: string): Promise<UserCredential> {
      // TODO: const { customToken } = await verifySmsOtp({ mobile, code });
      //       return signInWithCustomToken(auth, customToken);
      void confirmation;
      void code;
      // Referenced to document the intended call: signInWithCustomToken(auth, token)
      void signInWithCustomToken;
      void auth;
      notImplemented(providerName);
    },
  };
}

export const msg91Provider: OtpProvider = makeGatewayProvider('msg91');
export const fast2smsProvider: OtpProvider = makeGatewayProvider('fast2sms');
export const whatsappProvider: OtpProvider = makeGatewayProvider('whatsapp');
